import { useState, useCallback } from 'react';
import { useUpload } from './useUpload';
import api from '../utils/api';

interface UseUploadImageVisibilityReturn {
  toggleVisibility: (imageId: string) => Promise<void>;
  setAllVisibility: (visibility: 'public' | 'private') => Promise<void>;
  error: string | null;
}

/**
 * Hook for toggling per-image visibility on the upload page.
 *
 * Uses optimistic updates with rollback on failure, following the same
 * pattern as useObjectImages. When catalogId is null (user hasn't uploaded
 * yet), toggleVisibility sets an error message instead of silently failing.
 */
export function useUploadImageVisibility(ownerId?: string | null): UseUploadImageVisibilityReturn {
  const { catalogId, uploadedImages, updateImageVisibility } = useUpload();
  const [error, setError] = useState<string | null>(null);

  const toggleVisibility = useCallback(
    async (imageId: string): Promise<void> => {
      if (!catalogId) {
        setError('Cannot change visibility until images are uploaded');
        return;
      }

      const image = uploadedImages.find(
        (img: { imageId: string }) => img.imageId === imageId
      );
      if (!image) {
        setError(`Image not found: ${imageId}`);
        return;
      }

      const currentVisibility = (image as { visibility?: 'public' | 'private' }).visibility || 'private';
      const newVisibility: 'public' | 'private' = currentVisibility === 'public' ? 'private' : 'public';

      // Optimistic update
      updateImageVisibility(imageId, newVisibility);
      setError(null);

      const base = `/api/catalogs/${catalogId}/images/${imageId}/visibility`;
      const url = ownerId ? `${base}?owner_id=${ownerId}` : base;

      try {
        await api.patch(url, {
          visibility: newVisibility,
        });
      } catch (err) {
        // Revert on error
        updateImageVisibility(imageId, currentVisibility);
        const message = err instanceof Error ? err.message : 'Failed to update image visibility';
        setError(message);
      }
    },
    [catalogId, uploadedImages, updateImageVisibility, ownerId]
  );

  const setAllVisibility = useCallback(
    async (visibility: 'public' | 'private'): Promise<void> => {
      if (!catalogId) {
        setError('Cannot change visibility until images are uploaded');
        return;
      }

      const imagesToUpdate = uploadedImages.filter(
        (img: { imageId: string; visibility?: 'public' | 'private' }) =>
          (img.visibility || 'private') !== visibility
      );
      if (imagesToUpdate.length === 0) return;

      // Optimistic update all
      imagesToUpdate.forEach((img: { imageId: string }) =>
        updateImageVisibility(img.imageId, visibility)
      );
      setError(null);

      // Call API for each, rollback failures
      const results = await Promise.allSettled(
        imagesToUpdate.map((img: { imageId: string }) => {
          const base = `/api/catalogs/${catalogId}/images/${img.imageId}/visibility`;
          const url = ownerId ? `${base}?owner_id=${ownerId}` : base;
          return api.patch(url, { visibility });
        })
      );

      const failures = results
        .map((r, i) => (r.status === 'rejected' ? imagesToUpdate[i] : null))
        .filter(Boolean);

      if (failures.length > 0) {
        const previousVisibility = visibility === 'public' ? 'private' : 'public';
        failures.forEach((img) => updateImageVisibility(img!.imageId, previousVisibility));
        setError(`Failed to update ${failures.length} image(s)`);
      }
    },
    [catalogId, uploadedImages, updateImageVisibility, ownerId]
  );

  return { toggleVisibility, setAllVisibility, error };
}
